import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { IScaleMovementQuery, IScaleQuery } from "../../models/scale/query";
import {
  IScale,
  IScaleDetails,
  IScaleLight,
  IScaleMovement,
  IScalePendingChanges,
  IScalePendingChangesSummary,
  IScaleSlot,
} from "../../models/scale/response";
import { ICreateScale, IUpdateScale } from "../../models/scale/request";

class ScaleService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  getAllScales = (query: IScaleQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IScale>>({
      path: `/scales`,
      method: "GET",
      query,
      ...params,
    });

  getAllScalesLight = (query: IScaleQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IScaleLight>>({
      path: `/scales`,
      method: "GET",
      query: { ...query, light: true },
      ...params,
    });

  getScale = (id: number, params?: RequestParams) =>
    this.http.request<IScaleDetails>({
      path: `/scales/${id}`,
      method: "GET",
      ...params,
    });

  createScale = (data: ICreateScale, params?: RequestParams) =>
    this.http.request<IScaleDetails>({
      path: `/scales`,
      method: "POST",
      body: data,
      ...params,
    });

  updateScale = (id: number, data: IUpdateScale, params?: RequestParams) =>
    this.http.request<IScaleDetails>({
      path: `/scales/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteScale = (id: number, params?: RequestParams) =>
    this.http.request<IScale>({
      path: `/scales/${id}`,
      method: "DELETE",
      ...params,
    });

  /** Every row of the device's table, empty slots included — the screen is the
   *  file's preview, so there is nothing to paginate or filter here. */
  getScaleSlots = (id: number, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IScaleSlot>>({
      path: `/scales/${id}/slots`,
      method: "GET",
      ...params,
    });

  /** `productId` adds the slot that product already occupies, so an edit form
   *  can show it as the selected value without patching the list by hand. */
  getScaleFreeSlots = (id: number, productId?: number, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<number>>({
      path: `/scales/${id}/free-slots`,
      method: "GET",
      query: productId ? { productId } : undefined,
      ...params,
    });

  /** `productId` is `product.id`. Nothing ever leaves a scale on its own except
   *  the four events the backend handles, so this is the manual way off. */
  detachScaleProduct = (id: number, productId: number, params?: RequestParams) =>
    this.http.request<void>({
      path: `/scales/${id}/products/${productId}`,
      method: "DELETE",
      ...params,
    });

  getScaleMovements = (
    id: number,
    query: IScaleMovementQuery,
    params?: RequestParams,
  ) =>
    this.http.request<IBaseListingResponse<IScaleMovement>>({
      path: `/scales/${id}/movements`,
      method: "GET",
      query,
      ...params,
    });

  /** How far the device has drifted from the platform since it was last fed. */
  getScalePendingChanges = (id: number, params?: RequestParams) =>
    this.http.request<IScalePendingChanges>({
      path: `/scales/${id}/pending-changes`,
      method: "GET",
      ...params,
    });

  /** One call for a whole page of rows: the count needs every slot of every
   *  scale, which is why it is not a field on the list itself. Max 100 ids. */
  getScalesPendingChanges = (scaleIds: number[], params?: RequestParams) =>
    this.http.request<IScalePendingChangesSummary[]>({
      path: `/scales/pending-changes`,
      method: "GET",
      query: { scaleIds: scaleIds.join(",") },
      ...params,
    });

  /** Direct download, not a background report. A 422 arrives as a blob too, and
   *  the file's real name is in `Content-Disposition`, not ours to invent. */
  exportScale = (id: number, params?: RequestParams) =>
    this.http.request<Blob>({
      path: `/scales/${id}/export`,
      method: "GET",
      format: "blob",
      suppressErrorNotification: true,
      ...params,
    });

  /** Downloading is not feeding: the badge stays lit until someone confirms
   *  they walked the file to the device. */
  markScaleFed = (id: number, params?: RequestParams) =>
    this.http.request<IScaleDetails>({
      path: `/scales/${id}/mark-fed`,
      method: "POST",
      ...params,
    });
}

export default ScaleService;
